import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../api/client';
import { Button, EmptyState, StatusBadge } from '../components/common';
import { TodoDetailsModal } from '../components/modals';
import { formatDate } from '../utils/formatters';

/**
 * CompletedTodos page - View all completed tasks for a meeting
 */
const CompletedTodos = ({ meeting, user, onBack }) => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTodo, setSelectedTodo] = useState(null);

  useEffect(() => {
    loadTodos();
  }, [meeting.id]);

  const loadTodos = async () => {
    try {
      const data = await api.get(`/todos/meeting/${meeting.id}`);
      setTodos(data.filter(t => t.status === 'Done'));
    } catch (error) {
      console.error('Failed to load todos:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-header">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Completed Tasks - {meeting.name}</h1>
        <Button variant="secondary" onClick={onBack}>
          ← Back to Meeting
        </Button>
      </div>

      {todos.length === 0 ? (
        <EmptyState
          icon="📋"
          title="No completed tasks"
          message="Complete some tasks to see them here"
        />
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Assigned To</th>
              <th>Due Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {todos.map(todo => (
              <tr 
                key={todo.id} 
                style={{ cursor: 'pointer' }} 
                onClick={() => setSelectedTodo(todo)}
              >
                <td>{todo.title}</td>
                <td>
                  {(todo.assignees || []).map(a => `${a.firstName} ${a.lastName}`).join(', ') || 'Unassigned'}
                </td>
                <td>{formatDate(todo.dueDate)}</td>
                <td>
                  <StatusBadge status={todo.status} type="todo" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Todo Details Modal */}
      {selectedTodo && (
        <TodoDetailsModal
          todo={selectedTodo}
          users={[]} // Users not needed for view-only modal
          onClose={() => setSelectedTodo(null)}
        />
      )}
    </div>
  );
};

CompletedTodos.propTypes = {
  meeting: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default CompletedTodos;
